import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";

const WINDOW_WIDTH = 480;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 160;

const Frame = styled.div`
    ${p => p.$active ? `
        position: fixed;
        width: ${WINDOW_WIDTH}px;
        max-width: calc(100vw - 16px);
        display: flex;
        flex-direction: column;
        background: #111;
        border: 1px solid #333;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
        overflow: hidden;

        video {
            flex: 1 1 auto;
            min-height: 0;
            max-height: ${p.$sized ? "none" : "60vh"};
        }
    ` : "display: contents;"}
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background: #222;
    color: #eee;
    font-size: 13px;
    cursor: move;
    user-select: none;
`;

const Title = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CloseButton = styled.button`
    display: flex;
    border: none;
    background: transparent;
    color: #eee;
    padding: 0;
`;

const ResizeHandle = styled.div`
    position: absolute;
    right: 0;
    bottom: 0;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;
    background: linear-gradient(135deg, transparent 50%, #888 50%);
`;

export const FloatingWindow = ({ active = true, title, onClose, zIndex = 1040, defaultHeight = null, children }) => {
    const [position, setPosition] = useState(null);
    const [size, setSize] = useState({ width: WINDOW_WIDTH, height: defaultHeight });
    const windowRef = useRef(null);
    const dragOffset = useRef(null);
    const resizeStart = useRef(null);

    useEffect(() => {
        const handleMove = (e) => {
            if (resizeStart.current) {
                const { startX, startY, left, width, height } = resizeStart.current;
                const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - left - 8);
                const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - resizeStart.current.top - 8);
                setSize({
                    width: Math.min(Math.max(MIN_WIDTH, width + e.clientX - startX), maxWidth),
                    height: Math.min(Math.max(MIN_HEIGHT, height + e.clientY - startY), maxHeight),
                });
                return;
            }
            if (!dragOffset.current || !windowRef.current) return;
            const { offsetX, offsetY } = dragOffset.current;
            const { offsetWidth, offsetHeight } = windowRef.current;
            const maxX = Math.max(0, window.innerWidth - offsetWidth);
            const maxY = Math.max(0, window.innerHeight - offsetHeight);
            setPosition({
                x: Math.min(Math.max(0, e.clientX - offsetX), maxX),
                y: Math.min(Math.max(0, e.clientY - offsetY), maxY),
            });
        };
        const handleUp = () => {
            dragOffset.current = null;
            resizeStart.current = null;
        };
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
        };
    }, []);

    const startDrag = (e) => {
        if (e.target.closest("button") || !windowRef.current) return;
        const rect = windowRef.current.getBoundingClientRect();
        dragOffset.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
        e.preventDefault();
    };

    const startResize = (e) => {
        if (!windowRef.current) return;
        const rect = windowRef.current.getBoundingClientRect();
        resizeStart.current = {
            startX: e.clientX,
            startY: e.clientY,
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
        };
        setPosition({ x: rect.left, y: rect.top });
        e.preventDefault();
        e.stopPropagation();
    };

    const placement = position
        ? { left: position.x, top: position.y }
        : { left: 24, bottom: 24 };

    return (
        <Frame
            ref={windowRef}
            $active={active}
            $sized={size.height != null}
            style={active ? { ...placement, zIndex, width: size.width, height: size.height || undefined } : undefined}
        >
            {active && (
                <Header onMouseDown={startDrag} title="Drag to move">
                    <Title>{title}</Title>
                    <CloseButton type="button" title="Close player" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </CloseButton>
                </Header>
            )}
            {children}
            {active && <ResizeHandle title="Drag to resize" onMouseDown={startResize} />}
        </Frame>
    );
};

const FloatingVideoPlayer = ({ name, src, onClose }) => (
    <FloatingWindow active title={name} onClose={onClose}>
        <video
            src={src}
            controls
            autoPlay
            preload="metadata"
            style={{ width: "100%", display: "block", background: "#000", objectFit: "contain" }}
        />
    </FloatingWindow>
);

export default FloatingVideoPlayer;
